import { animated, useSpring } from "@react-spring/three";
import { useGLTF } from "@react-three/drei";
import React, { useContext, useEffect } from "react";
import {
  BoltClosePos,
  BoltOpenPosFront,
  Deg0,
  DoorDeg90CCW,
  HandleDeg90CW,
  Scale0,
  Scale1
} from "../../utils/AnimaionSprings";
import CurrentContext from "../../utils/CurrentContext";
import useChangedTexture from "../../utils/customHooks/useChangedTexture";
import useIsMobile from "../../utils/customHooks/useIsMobile";
import useTextureCustom from "../../utils/customHooks/useTextureCustom";
import {
  BlackPlastic,
  BoltMaterial,
  PaintMaterial,
} from "../../utils/finsishesData";
import { handleExportGLTF } from "../../utils/jsHelper";

//region Default options
const defaultoptions = [
  {
    materialName: "body_paint",
    finishToApply: "texturedblack",
  },
  {
    materialName: "interior_fabric",
    finishToApply: "grayfabric",
  },
  {
    materialName: "interior_parts",
    finishToApply: "ammocan_interior_parts",
  },
  {
    materialName: "handle",
    finishToApply: "ammocan_handle",
  },
];

const HandleUpdateTexture = (item, materials) => {
  useTextureCustom(materials[item.materialName], item.finishToApply);
};

export function Ammocan({ animateInit, currentVariant }) {
  const isMobile = useIsMobile();
  const { nodes, materials, scene } = useGLTF(
    isMobile ? "models/ammocan-m.glb" : "models/ammocan.glb"
  );
  const mobileModel = useGLTF("models/ammocan-m.glb");

  if (isMobile) {
    return (
      <Model
        {...{
          nodes,
          materials,
          scene,
          animateInit,
          currentVariant,
          mobileProp: true,
        }}
      />
    );
  } else {
    return (
      <>
        <Model
          {...{
            nodes,
            materials,
            scene,
            animateInit,
            currentVariant,
          }}
        />
        <Model
          {...{
            nodes: mobileModel?.nodes,
            materials: mobileModel?.materials,
            scene: mobileModel?.scene,
            animateInit,
            currentVariant,
            visible: false,
            mobileProp: true,
          }}
        />
      </>
    );
  }
}

const Model = ({
  nodes,
  materials,
  scene,
  animateInit,
  currentVariant,
  visible = true,
  mobileProp = false,
}) => {
  console.log("c");
  const {
    setLoading,
    setIsARReady,
    setARModelSrc,
    setARUnicode,
    isAR,
    setIsAR,
    doorOpen,
  } = useContext(CurrentContext);

  //region animation springs
  const { rotation_Door } = useSpring({
    rotation_Door: doorOpen ? DoorDeg90CCW : Deg0,
    config: { duration: 2000 },
    delay: 1250,
  });

  const { handle_Rotation1 } = useSpring({
    handle_Rotation1: doorOpen ? HandleDeg90CW : Deg0,
    config: { duration: 1000 },
    delay: doorOpen ? 0 : 3250,
  });

  const { handle_Rotation2 } = useSpring({
    handle_Rotation2: doorOpen ? Deg0 : HandleDeg90CW,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  const { handle_Scale1 } = useSpring({
    handle_Scale1: doorOpen ? Scale0 : Scale1,
    config: { duration: 1 },
    delay: 2250,
  });

  const { handle_Scale2 } = useSpring({
    handle_Scale2: doorOpen ? Scale1 : Scale0,
    config: { duration: 1 },
    delay: 2250,
  });

  const { position_Bolt_Front } = useSpring({
    position_Bolt_Front: doorOpen ? BoltOpenPosFront : BoltClosePos,
    config: { duration: 1000 },
    delay: doorOpen ? 3250 : 0,
  });

  useEffect(() => {
    if (isAR && mobileProp) {
      handleExportGLTF({
        scene,
        fileName: "ammocan",
        setLoading,
      }).then(({ file, unicode } = {}) => {
        setARModelSrc(file);
        setIsARReady(true);
        setARUnicode(unicode);
      });
    }
    setIsAR(false);
  }, [isAR, mobileProp]);

  defaultoptions.map((item) => {
    HandleUpdateTexture(item, materials);
  });

  const materialNames = {
    interior_materials: "interior_fabric",
    outer_materials: "body_paint",
    hardware_materials: ["handle"],
  };

  useChangedTexture(
    currentVariant,
    materials,
    materialNames,
    HandleUpdateTexture
  );
  useTextureCustom(PaintMaterial, currentVariant.outer);

  const decalOuterColor = "#515151";

  materials.door_decal.color.set(decalOuterColor);

  return (
    <group dispose={null} visible={visible}>
      <animated.group
        position={[0.24, 0.068, 0.256544]}
        rotation={rotation_Door}
      >
        {/* Bolts */}
        <animated.mesh
          castShadow
          geometry={nodes.bolts_front.geometry}
          material={BoltMaterial}
          position={position_Bolt_Front}
        />
        <mesh
          castShadow
          geometry={nodes.bolts_side_housing.geometry}
          material={BlackPlastic}
        />
        <mesh
          castShadow
          geometry={nodes.handle.geometry}
          material={materials.handle}
        />
        <mesh
          geometry={nodes.inner_door.geometry}
          material={materials.interior_fabric}
        />
        <animated.mesh
          rotation={handle_Rotation1}
          scale={handle_Scale1}
          castShadow
          geometry={nodes.key.geometry}
          material={materials.key}
          position={[-0.241, 0.85, -0.013]}
        />
        <animated.mesh
          rotation={handle_Rotation2}
          scale={handle_Scale2}
          castShadow
          geometry={nodes.key.geometry}
          material={materials.key}
          position={[-0.241, 0.85, -0.013]}
        />
        {/* Door exterior */}
        <mesh
          castShadow
          geometry={nodes.outer_door.geometry}
          material={PaintMaterial}
        />
        <mesh
          position={[0, 0, 0.01]}
          geometry={nodes.door_decal.geometry}
          material={materials.door_decal}
        />
      </animated.group>
      <mesh
        castShadow
        geometry={nodes.interior.geometry}
        material={materials.interior_fabric}
      />
      <mesh
        receiveShadow
        geometry={nodes.outer_body.geometry}
        material={PaintMaterial}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_1.geometry}
        material={materials.Door_Bolts_Plastic}
      />
      <mesh
        castShadow
        receiveShadow
        geometry={nodes.interior_parts_2.geometry}
        material={materials.Shelf_Border_Horizontal}
      />
    </group>
  );
};

//useGLTF.preload("models/ammocan-v1.glb");
